import { mutationOptions } from '../../common/utils';
import { CART_QUERY_KEYS } from './keys';
import type {
  GetCartRequest,
  AddToCartRequest,
  UpdateCartItemRequest,
  DeleteCartRequest,
  ApplyCouponRequest,
  RemoveCouponRequest,
} from './types';
import { cartAPIClient } from '../cartAPIClient';

import { cartTokenQueue } from './cartTokenQueue';
import type { QueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';
import type { CartResponseResponse } from '../../../model/cart/cart';
import { persistCartSessionKey } from '../../../utils/sessionKeyPersistence';

export type CartServiceMutationProps = GetCartRequest & {
  queryClient: QueryClient;
};

const setGeneratedSessionKey = (data: CartResponseResponse['generatedSessionKey']) => {
  if (data?.key && data?.value) {
    // Persist via registered persistence (web: cookie, RN: AsyncStorage, etc.)
    void persistCartSessionKey({
      name: data.key,
      value: data.value,
    });
  }
};

export const TEMP_ITEM_KEY = 'TEMP_PRODUCT';

export const CART_QUERIES = {
  getCart: ({ currency, cartToken, sessionKey }: GetCartRequest) => {
    return queryOptions({
      queryKey: CART_QUERY_KEYS.getCart({ currency, sessionKey }),
      queryFn: () => cartAPIClient.getCart({ sessionKey, currency, cartToken }),
      select: (data) => {
        cartTokenQueue.enqueue(data.cartInfo.cartToken);
        setGeneratedSessionKey(data.generatedSessionKey);
        return data;
      },
    });
  },

  addToCart: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (payload: AddToCartRequest) => {
        return cartAPIClient.addToCart({
          ...payload,
          currency,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
        });
      },
      mutationKey: CART_QUERY_KEYS.addToCart(),
      onSuccess: (data) => {
        if (data.generatedSessionKey) {
          setGeneratedSessionKey(data.generatedSessionKey);
        }
        cartTokenQueue.enqueue(data.cartInfo.cartToken);
        queryClient.setQueryData(CART_QUERY_KEYS.getCart({ currency, sessionKey }), data);
      },
    });
  },

  updateCartItem: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: UpdateCartItemRequest) => {
        return cartAPIClient.updateCartItem({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.updateCartItem(),

      onSuccess: (data) => {
        if (data.generatedSessionKey) {
          setGeneratedSessionKey(data.generatedSessionKey);
        }
        cartTokenQueue.enqueue(data.cartInfo.cartToken);
        queryClient.setQueryData(CART_QUERY_KEYS.getCart({ currency, sessionKey }), data);
      },
    });
  },

  deleteCartItem: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: DeleteCartRequest) => {
        return cartAPIClient.deleteCartItem({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.removeCartItem(),
      onSuccess: (data) => {
        if (data.generatedSessionKey) {
          setGeneratedSessionKey(data.generatedSessionKey);
        }
        cartTokenQueue.enqueue(data.cartInfo.cartToken);
        queryClient.setQueryData(CART_QUERY_KEYS.getCart({ currency, sessionKey }), data);
      },
    });
  },

  applyCoupon: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: ApplyCouponRequest) => {
        const cartToken = request.cartToken || cartTokenQueue.peek();
        
        return cartAPIClient.applyCoupon({
          ...request,
          sessionKey,
          cartToken,
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.applyCoupon(),
      onSuccess: (data) => {
        if (data.generatedSessionKey) {
          setGeneratedSessionKey(data.generatedSessionKey);
        }
        cartTokenQueue.enqueue(data.cartInfo.cartToken);
        queryClient.setQueryData(CART_QUERY_KEYS.getCart({ currency, sessionKey }), data);
      },
    });
  },

  removeCoupon: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: RemoveCouponRequest) => {
        return cartAPIClient.removeCoupon({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.removeCoupon(),
      onSuccess: (data) => {
        if (data.generatedSessionKey) {
          setGeneratedSessionKey(data.generatedSessionKey);
        }
        cartTokenQueue.enqueue(data.cartInfo.cartToken);
        queryClient.setQueryData(CART_QUERY_KEYS.getCart({ currency, sessionKey }), data);
      },
    });
  },
};
